import {
    Recipient,
    RecipientRole,
    RecipientRoleStatus,
    Sender,
    SigningRequest,
    SigningRequestHistory,
    SigningRequestStatus,
} from '@microsoft/esign.gateway.service.client';
import { DocusignConstants } from '../common/DocusignConstants';
import { ApprovalRequestType as ApprovalProvider } from '../data/types.generated';
import {
    ApprovalType,
    ApprovalRequestType,
    GetApprovalDetailsControllerOutput,
    UserPrincipal,
    ApprovalResponseViewDefinition,
    ApprovalResponseType,
    ApprovalUserRole,
    ApprovalCreator,
} from '../service/gen';
import { WebhookBaseDataObject } from '../service/gen/esign';
import {
    AgreementState,
    CreateEnvelopeInput,
    DraftEnvelopeInput,
    ParticipantDetails,
    ParticipantRole,
} from '../service/gen/esign';
import { ActionType } from '../views/components/ApprovalAction';
import { SharePointESignStatus } from './EsignUtils';
import { getAppHost } from './TeamsUtils';
import { stringFormat } from './Utils';

declare const WEBHOOK_BASE_URL: string;
declare interface ESignAdditionalViewProperties {
    /** Gets the file attachment warning message. */
    FileAttachmentWarningMessage?: string | undefined;
    /** Gets the target folder uri. */
    TargetFolderUri?: string | undefined;
    /** Gets the internal status. */
    ProviderStatus?: string | undefined;
}

export const mapESignModelToApprovalModel = (
    data: SigningRequest,
    userId: string,
    approvalType: string
): GetApprovalDetailsControllerOutput => {
    if (!data) {
        return null;
    }
    const sender = getSender(data.sender);

    // Current user can be a combination of sender / signer / approver.
    const userRoles = [];
    if (data.recipients?.some((recipient: Recipient): boolean => recipient.userId === userId)) {
        userRoles.push(ApprovalUserRole.Approver);
    }

    if (data.sender?.userId === userId) {
        userRoles.push(ApprovalUserRole.Owner);
    }

    const esignReviewLinks = [{ Name: data.title, Link: data.url }];
    if (
        approvalType === ApprovalProvider.Docusign &&
        data.recipients?.some((recipient: Recipient): boolean => recipient.userId === userId)
    ) {
        esignReviewLinks.push({ Name: DocusignConstants.DocusignRecipientsView, Link: data.url });
    }

    if (approvalType === ApprovalProvider.Docusign && data.sender?.userId === userId) {
        esignReviewLinks.push({ Name: DocusignConstants.DocusignEditView, Link: data.url });
    }

    if (!shouldDisplayAttachment(new Date(), approvalType, data, userId)) {
        // hide the attachment link for the recipent if it has expired.
        data.url = null;
    }

    const approvalDetails: GetApprovalDetailsControllerOutput = {
        Name: data.title,
        Id: data.id,
        Properties: {
            IsActive: data.status === SigningRequestStatus.Sent,
            ApprovalType: ApprovalType.ESign,
            RequestType: ApprovalRequestType.Esign,
            Owner: sender,
            Creator: sender,
            Title: data.title,
            Details: data.message,
            Result: getResult(data.status, data.recipients, userId),
            Approvers: data.recipients?.map((recipient: Recipient): string => recipient.id),
            Principals: getPrincipals(data, sender),
            Stages: [
                {
                    Stage: 1,
                    Responses: getHistory(data.history, sender),
                },
            ],
            CreationDate: data.creationDateTime,
            Item: data.url && {
                Link: data.url,
                DisplayName: data.title,
            },
            EsignReviewLinks: esignReviewLinks,
            UserRoles: userRoles,
            Source: data.source,
            ExpirationDate: data.expirationDateTime,
            Type: ApprovalResponseType.BasicAwaitAll,
        },
    };

    const additionalViewProperties = getESignAdditionalViewProperties(data);
    if (additionalViewProperties) {
        approvalDetails.Properties.AdditionalViewProperties = additionalViewProperties;
    }
    return approvalDetails;
};

export const shouldDisplayAttachment = (
    currentDateTime: Date,
    approvalType: string,
    data: SigningRequest,
    currentUserId: string
): boolean => {
    const isExpired =
        data.urlExpirationDateTime &&
        data.sender?.userId !== currentUserId &&
        new Date(data.urlExpirationDateTime).getTime() < currentDateTime.getTime();
    const isCancelled = data.sender?.userId !== currentUserId && data.status === SigningRequestStatus.Voided;

    if (approvalType === ApprovalProvider.Syntex && (isExpired || isCancelled)) {
        return false;
    }
    return true;
};

export const getESignAdditionalViewProperties = (data: SigningRequest): ESignAdditionalViewProperties | undefined => {
    const fileAttachmentWarningMessage = getFileAttachmentWarningMessage(data.urlExpirationDateTime);
    const providerStatus = getProviderStatus(data);

    if (fileAttachmentWarningMessage || providerStatus) {
        return {
            FileAttachmentWarningMessage: fileAttachmentWarningMessage,
            ProviderStatus: providerStatus,
        };
    }
    return undefined;
};

export const getSender = (sender: Sender): UserPrincipal => {
    const { id, userId, fullName, email } = sender || {};
    const displayName = getDisplayName(fullName, email);
    return {
        Id: id ?? userId,
        DisplayName: displayName,
        Email: email,
        UserPrincipalName: displayName,
    };
};

export const getPrincipals = (signingRequest: SigningRequest, sender: UserPrincipal): UserPrincipal[] => {
    const principalsMap = new Map<string, UserPrincipal>();

    signingRequest.history?.forEach((history: SigningRequestHistory) => {
        const author = history.author;
        const displayName = getDisplayName(author.fullName, author.email);
        const principal: UserPrincipal = {
            Id: author.id ?? author.userId,
            DisplayName: displayName,
            Email: author.email,
            UserPrincipalName: displayName,
        };
        principalsMap.set(principal.Id, principal);
    });

    signingRequest.recipients?.forEach((recipient: Recipient) => {
        const displayName = getDisplayName(recipient.fullName, recipient.email);
        const principal: UserPrincipal = {
            Id: recipient.id ?? recipient.userId,
            DisplayName: displayName,
            Email: recipient.email,
            UserPrincipalName: displayName,
        };
        principalsMap.set(principal.Id, principal);
    });

    principalsMap.set(sender.Id, sender);

    return Array.from(principalsMap.values());
};

export const getHistory = (
    historyList: SigningRequestHistory[],
    sender: UserPrincipal
): ApprovalResponseViewDefinition[] => {
    // We received this history from the Gateway API: Creation in Draft -> Creation in Send -> n Send changes -> Signed OR Declined OR Voided
    // Note: The first change "Creation in Draft" is optional.
    // The history we want: n Send changes -> Signed OR Declined OR Voided
    // Conclusion: we remove the 2 first element of the history (if the first is for the draft creation) or the first element (if the first is not draft)
    if (historyList?.[0]?.status === SigningRequestStatus.Draft) {
        historyList.shift();
    }

    historyList?.shift();

    return (
        historyList?.map((history: SigningRequestHistory): ApprovalResponseViewDefinition => {
            return {
                CreationDate: history.date,
                Owner: getHistoryOwner(history.author, sender),
                Response: getResponse(history),
                Comments: history.comments,
                Order: history.author?.signingOrder ?? 0,
            };
        }) || []
    );
};

export const getHistoryOwner = (author: Recipient, sender: UserPrincipal): UserPrincipal => {
    // Return the sender if the author is undefined because:
    // The author field is only populated when the author is not the sender.
    // When the author is the sender, the field is undefined.
    if (!author) return sender;

    return {
        Id: author.id ?? author.userId,
        DisplayName: author.fullName,
        Email: author.email,
        UserPrincipalName: author.fullName,
    };
};

export const getActionTypeFromStatus = (status: RecipientRoleStatus | SigningRequestStatus): ActionType => {
    switch (status) {
        case RecipientRoleStatus.Approved:
            return ActionType.Approve;
        case RecipientRoleStatus.Declined:
        case SigningRequestStatus.Declined:
            return ActionType.Declined;
        case RecipientRoleStatus.Received:
            return ActionType.Request;
        case RecipientRoleStatus.Signed:
        case SigningRequestStatus.Signed:
            return ActionType.Signed;
        case RecipientRoleStatus.Waiting:
        case SigningRequestStatus.Sent:
            return ActionType.Pending;
        case SigningRequestStatus.Voided:
            return ActionType.Cancel;
        default:
            return null;
    }
};

export const getResponse = (history: SigningRequestHistory): string => {
    const status = history?.author ? history?.author.roleStatus : history?.status;
    const actionType = getActionTypeFromStatus(status);
    return actionType === ActionType.Cancel ? 'Canceled' : ActionType[actionType];
};

export const needsActionFromCurrentUser = (recipients: Recipient[], userId: string): boolean => {
    const currentUserAsRecipient = recipients?.find((recipient: Recipient): boolean => recipient.userId === userId);
    if (currentUserAsRecipient) {
        return (
            currentUserAsRecipient.roleStatus === RecipientRoleStatus.Waiting ||
            currentUserAsRecipient.roleStatus === RecipientRoleStatus.Received
        );
    } else {
        return false;
    }
};

export const getResult = (status: SigningRequestStatus, recipients: Recipient[], userId: string): string => {
    if (!status) {
        return 'Canceled';
    }

    return {
        Draft: '', // Draft is empty because Approval doesn't get any draft approval
        Sent: needsActionFromCurrentUser(recipients, userId) ? 'NeedsAction' : 'InProgress',
        Signed: 'Signed',
        Declined: 'Rejected',
        Voided: 'Canceled',
    }[status];
};

export const getDisplayName = (fullName: string, email: string): string => {
    return fullName ?? email;
};

export const mapCreateEnvelopeInputToSigningRequest = (createEnvelopeInput: CreateEnvelopeInput): SigningRequest => {
    const isTemplate = createEnvelopeInput.EsignItem.LibraryDocumentId != null;
    const AdobeWebhookPath = '/esignCallback/v1.0/Adobe';
    const url = GetUrl(AdobeWebhookPath, createEnvelopeInput.WebhookBaseDataObject);
    return {
        title: createEnvelopeInput.Title,
        message: createEnvelopeInput.Details,
        source: 'Approvals',
        recipients: createEnvelopeInput.ParticipantDetails.map(
            (participantDetails: ParticipantDetails): Recipient => mapParticipantDetailsToRecipient(participantDetails)
        ),
        documents: [
            {
                libraryDocumentId: isTemplate
                    ? createEnvelopeInput.EsignItem.LibraryDocumentId
                    : createEnvelopeInput.EsignItem.TransientDocumentId,
                isTemplate,
            },
        ],
        status:
            createEnvelopeInput.State === AgreementState.In_Process
                ? SigningRequestStatus.Sent
                : SigningRequestStatus.Draft,
        webhookUrl: url.toString(),
    };
};

export const mapDraftEnvelopeInputToSigningRequest = (draftEnvelopeInput: DraftEnvelopeInput): SigningRequest => {
    const isTemplate = draftEnvelopeInput.TemplateId != null;
    const DocusignWebhookPath = '/esignCallback/v2.0/Docusign';
    const url = GetUrl(DocusignWebhookPath, draftEnvelopeInput.WebhookBaseDataObject);
    return {
        title: draftEnvelopeInput.Title,
        message: draftEnvelopeInput.Details,
        source: 'Approvals',
        recipients: draftEnvelopeInput.ParticipantDetails.map(
            (participantDetails: ParticipantDetails): Recipient => mapParticipantDetailsToRecipient(participantDetails)
        ),
        documents: [
            {
                libraryDocumentId: isTemplate ? draftEnvelopeInput.TemplateId : draftEnvelopeInput.Item.Link,
                name: draftEnvelopeInput.DocumentName,
                isTemplate,
            },
        ],
        status: draftEnvelopeInput.IsDraft ? SigningRequestStatus.Draft : SigningRequestStatus.Sent,
        webhookUrl: url.toString(),
    };
};

function GetUrl(webhookPath: string, webhookBaseDataObject: WebhookBaseDataObject): URL {
    const url = new URL(WEBHOOK_BASE_URL + webhookPath);
    const host = getAppHost();
    if (webhookBaseDataObject == null || webhookBaseDataObject.ApprovalCreator === ApprovalCreator.PersonalApp) {
        url.searchParams.set('TenantId', host.getTenantId());
        url.searchParams.set('ApprovalCreator', ApprovalCreator.PersonalApp.toString());
        url.searchParams.set('AadUserObjectId', host.getUserObjectId());
    } else {
        url.searchParams.set('TenantId', host.getTenantId());
        url.searchParams.set('ServiceUrl', webhookBaseDataObject.ServiceUrl);
        url.searchParams.set('MessageId', webhookBaseDataObject.MessageId);
        url.searchParams.set('ConversationId', webhookBaseDataObject.ConversationId);
        url.searchParams.set('ChannelId', webhookBaseDataObject.ChannelId);
        url.searchParams.set('ApprovalCreator', webhookBaseDataObject.ApprovalCreator.toString());
        url.searchParams.set('AadGroupId', webhookBaseDataObject.AadGroupId);
        url.searchParams.set('AadUserObjectId', host.getUserObjectId());
        url.searchParams.set('Mri', webhookBaseDataObject.MRI);
        url.searchParams.set('Locale', host.getLocale().getCurrentLocale().toString());
        url.searchParams.set('ApprovalType', webhookBaseDataObject.ApprovalType.toString());
    }
    return url;
}

export const mapParticipantDetailsToRecipient = (participantDetails: ParticipantDetails): Recipient => {
    return {
        email: participantDetails.Email,
        fullName: participantDetails.FullName,
        signingOrder: participantDetails.Order,
        role: mapParticipantRoleToRecipientRole(participantDetails.Role),
    };
};

export const mapParticipantRoleToRecipientRole = (participantRole: ParticipantRole): RecipientRole => {
    switch (participantRole) {
        case ParticipantRole.SIGNER:
            return RecipientRole.Signer;
        case ParticipantRole.CC:
            return RecipientRole.CarbonCopy;
        case ParticipantRole.APPROVER:
            return RecipientRole.Approver;
        default:
            throw new Error(`Cannot map ParticipantRole ${participantRole.toString()}`);
    }
};

export const getFileAttachmentWarningMessage = (expirationDateTime: string): string => {
    if (expirationDateTime) {
        const loc = getAppHost().getLocale();
        const localeDate = new Date(expirationDateTime).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        return stringFormat(loc.strings.sharing_link_expiry_message, {
            date: localeDate,
        });
    }
    return null;
};

export const getProviderStatus = (data: SigningRequest): string | null => {
    if (
        data.status === SigningRequestStatus.Sent &&
        data.recipients?.every((recipient: Recipient): boolean => recipient.roleStatus === RecipientRoleStatus.Signed)
    ) {
        return SharePointESignStatus.PendingCompletion;
    }
    return null;
};
